import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, forkJoin } from 'rxjs';
import { share, map, catchError, switchMap, mergeMap } from 'rxjs/operators';
import { AgreementsAction } from './agreements.action';
import { BackendService } from '../../services/backend/backend.service';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { RoutesTable } from '../../resources/routes-table.model';
import { ErrorsType } from '../../enum/errors.type';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';
import { NetworkService } from '../../services/network.services';
import { ApiProvider } from '../../../shared/services/new-http/providers/api.http.service';
import { SettingsService } from '../../../shared/services/settings/settings.service';
import { AgreementsApiVersion } from '../../../shared/enum/agreement/agreements-options.enum';
import { HttpHeaders } from '@angular/common/http';


@Injectable()
export class AgreementsEffects {


    private authenticationToken: string;
    private sessionIDUSer: any;


    get headers(): HttpHeaders {
        this.store.select(s => s.authentication).subscribe(data => {
            this.authenticationToken = data.currentUser ? data.currentUser.token : null;
            this.sessionIDUSer = data.sessionID ? data.sessionID : '';
        });
        if (this.authenticationToken) {
            return new HttpHeaders({
                'Content-Type': 'application/json; charset=UTF-8',
                'Authorization': `Bearer ${this.authenticationToken}`,
            });
        }
        else {
            return new HttpHeaders({
                'Content-Type': 'application/json; charset=UTF-8',
                'X-SessionID': `${this.sessionIDUSer}`,
            });
        }
    }

    /** Headers específicos para finalizeRegistration que precisa de Authorization + X-SessionID */
    get finalizeRegistrationHeaders(): HttpHeaders {
        this.store.select(s => s.authentication).subscribe(data => {
            this.authenticationToken = data.currentUser ? data.currentUser.token : null;
            this.sessionIDUSer = data.sessionID ? data.sessionID : '';
        });

        let headers = new HttpHeaders({
            'Content-Type': 'application/json; charset=UTF-8',
        });

        if (this.authenticationToken) {
            headers = headers.set('Authorization', `Bearer ${this.authenticationToken}`);
        }

        if (this.sessionIDUSer) {
            headers = headers.set('X-SessionID', this.sessionIDUSer);
        }

        return headers;
    }


    get apiVersion(): AgreementsApiVersion {

        const settings =
            this.settingsService.getConfigurationKeyValue("backendApiVersion");

        if (!settings) {
            return AgreementsApiVersion.VERSION_1;
        }

        return settings.agreement;
    }


    /** Effect: Recupera um agreement */
    getAgreement$ = createEffect(() => this.actions$
        .pipe(
          ofType(AgreementsAction.getAgreement),
            switchMap((action: any) => {
                return this.getAgreement(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: AgreementsAction.getAgreementSuccess, payload: result.value };
                            }
                            return { type: AgreementsAction.getAgreementSuccess, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: AgreementsAction.getAgreementError });
                        }),
                    );
            }),
            share(),
        ));

    /** Effect: Recupera um agreement e adiciona na lista */
    getAgreementData$ = createEffect(() => this.actions$
        .pipe(
            ofType(AgreementsAction.getAgreementData),
            mergeMap((action: any) => {
                return this.getAgreement(action.payload)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: AgreementsAction.getAgreementDataSuccess, payload: result.value };
                            }

                            return { type: AgreementsAction.getAgreementDataError, payload: result.value };
                        }),
                        catchError((error) => {
                            return of({ type: AgreementsAction.getAgreementDataError })
                        }),
                    );
            }),
            share(),
        ));


    /** Effect: Aceitar contrato */
    acceptAgreement$ = createEffect(() => this.actions$
        .pipe(
            ofType(AgreementsAction.acceptAgreement),
            mergeMap((action: any) => {
                return this.acceptAgreement(action.payload)
                .pipe(
                    map((payload) => {
                        if (payload.hasError) {
                            return {
                                type: AgreementsAction.acceptAgreementError,
                                payload: { type: ErrorsType.Error, value: payload.messages }
                            }
                        }
                        if (payload.ok && payload.hasValue) {
                            // Enviar apenas o objeto do contrato aceito
                            return {
                                type: AgreementsAction.acceptAgreementSuccess,
                                payload: {
                                    agreementID: action.payload.agreementID,
                                    agreementVersionID: action.payload.agreementVersionID,
                                    ...payload.value // dados do servidor
                                }
                            }
                        }
                        return {
                            type: AgreementsAction.acceptAgreementSuccess,
                            payload: {
                                agreementID: action.payload.agreementID,
                                agreementVersionID: action.payload.agreementVersionID,
                            }
                        };
                    }),
                    catchError((error) => of({
                        type: AgreementsAction.acceptAgreementError,
                        payload: error
                    })),
                );
            }),
            share(),
        ));

    /** Effect: contratos já aceitos pelo usuário */
    getAccepted$ = createEffect(() => this.actions$
    .pipe(
          ofType(AgreementsAction.getAccepted),
            switchMap((action) => {
                return this.getAcceptedAgreements()
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: AgreementsAction.getAcceptedError,
                                    payload: {
                                        type: ErrorsType.Warning,
                                        value: result.messages,
                                    }
                                };
                            }
                            if (result.hasError) {
                                return { type: AgreementsAction.getAcceptedError, payload: { type: ErrorsType.Error, value: result.messages } };
                            }
                            if (result.ok && result.hasValue) {
                                return { type: AgreementsAction.getAcceptedSuccess, payload: result.value };
                            }
                            return { type: AgreementsAction.getAcceptedSuccess, payload: result.value };
                        }),
                        catchError((error) => of({ type: AgreementsAction.getAcceptedError, payload: error })),
                    );
            }),
            share(),
    ));

    downloadAgreement$ = createEffect(() => this.actions$
    .pipe(
      ofType(AgreementsAction.downloadAgreement),
            switchMap((action: any) => {
                return this.backendService.get<DefaultResponse>(`corporation/file?fileCode=${action.payload}`)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                this.networkService.downloadFile(result.value, result.value.name);
                                return { type: AgreementsAction.downloadAgreementSuccess, payload: result.value };
                            }
                            return { type: AgreementsAction.downloadAgreementError, payload: result.value };
                        }),
                        catchError((error) => of({ type: AgreementsAction.downloadAgreementError, payload: error }))
                    );
            }),
            share(),
        ));

    acceptMultipleAgreements$ = createEffect(() => this.actions$
        .pipe(
            ofType(AgreementsAction.acceptMultipleAgreements),
            mergeMap((action: any) => {
                return this.acceptMultipleAgreements(action.payload)
                    .pipe(
                        map((result) => {
                            const allResponsesAreOk = (result as any)
                            .reduce((allOk, { ok }) =>  ok && allOk, true);

                            if (allResponsesAreOk) {
                                return {
                                    type: AgreementsAction.acceptMultipleAgreementsSuccess,
                                    payload: (result as any).reduce((msgs, { messages }) => [...messages, ...msgs], []),
                                };
                            }

                            return {
                                type: AgreementsAction.acceptMultipleAgreementsError,
                                payload: (result as any).filter(({ ok }) => !ok),
                            };
                        }),
                        //Dispara erro para UI caso a requisição falhe 
                        catchError((error) => {
                            return of({
                                type: AgreementsAction.acceptMultipleAgreementsError,
                                payload: {
                                    type: ErrorsType.Error,
                                    value: error.message || 'Erro ao aceitar contratos. Verifique sua conexão e tente novamente.'
                                }
                            });
                        })
                    );
            }),
            // Utiliza share() para evitar múltiplas execuções da mesma ação caso seja disparada mais de uma vez em um curto período
            share(),
        ));

    getMultipleAgreementsData$ = createEffect(() => this.actions$
        .pipe(
            ofType(AgreementsAction.getMultipleAgreementsData),
            mergeMap((action: any) => {
                return this.getMultipleAgreementsData(action.payload)
                    .pipe(
                        map((result) => {
                            const allResponsesAreOk = (result as any)
                            .reduce((allOk, { ok }) =>  ok && allOk, true);

                            if (allResponsesAreOk) {
                                return {
                                    type: AgreementsAction.getMultipleAgreementsDataSuccess,
                                    payload: (result as any).map(({ value }) => value),
                                };
                            }

                            return {
                                type: AgreementsAction.getMultipleAgreementsDataError,
                                payload: (result as any).filter(({ ok }) => !ok),
                            };
                        }),
                        catchError((error) => {
                            return of({
                                type: AgreementsAction.getMultipleAgreementsDataError,
                                payload: {
                                    type: ErrorsType.Error,
                                    value: 'Erro ao carregar contratos. Atualize a página e tente novamente.'
                                }
                            });
                        })
                    );
            }),
    ));


    getAgreementStatus$ = createEffect(() => this.actions$
        .pipe(
            ofType(AgreementsAction.getAgreementStatus),
            switchMap((action) => {
                return this.getAgreementStatus()
                    .pipe(
                        map((result) => {
                            if (result.hasAlerta) {
                                return {
                                    type: AgreementsAction.getAgreementStatusError,
                                    payload: {
                                        type: ErrorsType.Warning,
                                        value: result.messages,
                                    }
                                };
                            }
                            if (result.hasError) {
                                return {
                                    type: AgreementsAction.getAgreementStatusError,
                                    payload: { type: ErrorsType.Error, value: result.messages }
                                };
                            }
                            if (result.ok && result.hasValue) {
                                return { type: AgreementsAction.getAgreementStatusSuccess, payload: result.value };
                            }
                            return { type: AgreementsAction.getAgreementStatusSuccess, payload: result.value };
                        }),
                        catchError((error) => of({ type: AgreementsAction.getAgreementStatusError, payload: error })),
                    );
            }),
            share(),
    ));

    /** Effect: Finaliza o cadastro - aceita agreements e atualiza usuário em uma única chamada */
    finalizeRegistration$ = createEffect(() => this.actions$
        .pipe(
            ofType(AgreementsAction.finalizeRegistration),
            mergeMap((action: any) => {
                return this.finalizeRegistration(action.payload)
                    .pipe(
                        map((result: any) => {
                            if (result.ok && !result.hasError) {
                                return {
                                    type: AgreementsAction.finalizeRegistrationSuccess,
                                    payload: result.value,
                                };
                            }

                            return {
                                type: AgreementsAction.finalizeRegistrationError,
                                payload: {
                                    type: ErrorsType.Error,
                                    value: result.value || result.messages,
                                    step: result.value?.step,
                                }
                            };
                        }),
                        catchError((error) => {
                            return of({
                                type: AgreementsAction.finalizeRegistrationError,
                                payload: {
                                    type: ErrorsType.Error,
                                    value: error.message || 'Erro ao finalizar cadastro. Verifique sua conexão e tente novamente.'
                                }
                            });
                        })
                    );
            }),
            share(),
        ));

    /** Effect: Retry para carregar um contrato específico que falhou */
    retrySingleAgreementData$ = createEffect(() => this.actions$
        .pipe(
            ofType(AgreementsAction.retrySingleAgreementData),
            mergeMap((action: any) => {
                const contractGuid = action.payload as string;
                return this.getAgreement(contractGuid)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return {
                                    type: AgreementsAction.retrySingleAgreementDataSuccess,
                                    payload: result.value,
                                };
                            }
                            return {
                                type: AgreementsAction.retrySingleAgreementDataError,
                                payload: { contractGuid, error: result.value },
                            };
                        }),
                        catchError((error) => {
                            return of({
                                type: AgreementsAction.retrySingleAgreementDataError,
                                payload: { contractGuid, error: error.message || 'Erro ao carregar contrato.' },
                            });
                        })
                    );
            }),
            share(),
        ));

    constructor(
        private actions$: Actions,
        private backendService: BackendService,
        private store: Store<AppState>,
        private networkService: NetworkService,
        private api: ApiProvider,
        private settingsService: SettingsService,
    ) { }
    /**
     * Aceita um contrato
     * @param data dados
     */
    private acceptAgreement(agreement: any) {
        if (this.apiVersion === AgreementsApiVersion.VERSION_1_1) {
            return this.api.post<any>(
                RoutesTable.agreementV1_1.routes.acceptAgreement.uri, agreement, { headers: this.headers }
            );
        }
        return this.backendService.post<any>('agreement/accept', agreement);
    }

    /**
     * Recupera um agreement com base em seu id
     * @param agreementId id do contrato
     */
    private getAgreement(agreementId: number | string) {
        if (this.apiVersion === AgreementsApiVersion.VERSION_1_1) {
            return this.api.get<any>(
                RoutesTable.agreementV1_1.routes.getAgreement.uri + agreementId, { headers: this.headers }
            );
        }
        return this.backendService.get<DefaultResponse>(`agreement/${agreementId}`);
    }


    /**
     * Baixa um agreement com base em seu id
     * @param agreementId id do contrato
     */
    private acceptMultipleAgreements(agreements: number[] | string[]) {

        return forkJoin(
                ...agreements.map((agreementId: any) => this.acceptAgreement(agreementId))
            );
    }

    /**
     * Recupera os contratos já aceitos pelo usuário
     */
    private getAcceptedAgreements(): Observable<DefaultResponse> {
        if (this.apiVersion === AgreementsApiVersion.VERSION_1_1) {
            return this.api.get<any>(
                RoutesTable.agreementV1_1.routes.getAcceptedAgreements.uri, { headers: this.headers }
            );
        }

        return this.backendService.get<DefaultResponse>('agreement/accept');
    }

    /**
     * Recupera todos os contratos do usuário, com a informação de status
     * para cada contrato
     */
    private getAgreementStatus(): Observable<DefaultResponse> {
        return this.backendService.get<DefaultResponse>('agreement/status');
    }

    private getMultipleAgreementsData(agreementsId: number[]): Observable<DefaultResponse> {
        return forkJoin(
            ...agreementsId.map((agreementId) =>
            this.getAgreement(agreementId)));
    }

    /**
     * Finaliza o cadastro - aceita agreements e atualiza usuário em uma única chamada
     * @param payload { agreements: AgreementDTO[], userData: UpdateUserDTO }
     */
    private finalizeRegistration(payload: { agreements: any[], userData: any }): Observable<any> {
        return this.api.post<any>(
            RoutesTable.userV2.routes.finalizeRegistration.uri,
            payload,
            { headers: this.finalizeRegistrationHeaders }
        );
    }

}
